import React, { useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';

import api from '../../services/api';
import { useToast } from '../../hooks/toast';

import getResponseErrors from '../../utils/getResponseErrors';

import { IDadosRodape } from './index';
import { formataCPF, formataIdade } from '../../utils/funcoes';

export interface IPaciente {
  PacCodigo: number;
  PacNome: string;
  PacDataNasc: Date;
  PacSexo: string;
  PacDataNascFormatada: string;
  PacRG: string;
  PacCPF: string;
  PacIdade: string;
}

export interface IPedidoEspecialidade {
  PedCodigo: number;
  PedMedUsuCodigo?: number;
  PedPacCodigo: number;
  PedCliCodigo: number;
  PedIndicacao: string;
  PedCid: string;
  PedEspecialidade: string;
  PedHistorico: string;
  paciente: IPaciente;
  created_at: Date;
  dataEmissao: string;
  medico: {
    UsuCodigo: number;
    UsuNome: string;
    UsuCRM: string;
    UsuConselho: string;
    UsuEspecialidade: string;
  };
  perfil: {
    PerCodigo: number;
    PerNome: string;
  };
}

interface IComponentProps {
  PedCodigo: number;
  handleDadosRodape?(dadosRodape: IDadosRodape): void;
}

// impressao do pedido de exame mesmo sem estar agendado, somente com dados do exame em si
const PedidoEncaminhamentoNaoAgendado: React.FC<IComponentProps> = ({
  PedCodigo,
  handleDadosRodape,
}) => {
  const { addToast } = useToast();

  const [pedido, setPedido] = useState<IPedidoEspecialidade>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      try {
        const response = await api.get<IPedidoEspecialidade>(
          `/pedidoEspecialidade/impressao/${PedCodigo}`,
        );

        const dataNascimento = parseISO(
          response.data.paciente.PacDataNasc.toString(),
        );

        const dataEmissao = format(
          parseISO(response.data.created_at.toString()),
          'dd/MM/yyyy',
        );

        const dadosFormatados: IPedidoEspecialidade = {
          ...response.data,
          dataEmissao,
          paciente: {
            ...response.data.paciente,
            PacDataNasc: dataNascimento,
            PacDataNascFormatada: format(dataNascimento, 'dd/MM/yyyy'),
            PacIdade: formataIdade(dataNascimento, true),
            PacCPF: formataCPF(response.data.paciente.PacCPF),
          },
        };

        setPedido(dadosFormatados);
        if (handleDadosRodape)
          handleDadosRodape({
            CliCodigo: response.data.PedCliCodigo,
            assinatura: {
              nome: dadosFormatados.medico.UsuNome,
              especialidade: dadosFormatados.medico.UsuEspecialidade,
              documento: dadosFormatados.medico.UsuCRM,
              conselho: dadosFormatados.medico.UsuConselho,
            },
          });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setTimeout(() => {
          window.print();
        }, 2000);
      }
    }
    loadDados();
  }, [PedCodigo, addToast, handleDadosRodape]);

  return (
    <div style={{ fontSize: '1rem' }}>
      {pedido && (
        <>
          <h5 className="text-center mb-5">SOLICITAÇÃO DE ENCAMINHAMENTO</h5>
          <table
            className="table table-hover table-sm table-bordered"
            style={{ borderWidth: '0px' }}
          >
            <tbody style={{ fontSize: '1rem' }}>
              <tr>
                <td>
                  <b>NOME: </b> {pedido.paciente.PacNome}
                </td>
                <td>
                  <b>CPF: </b> {pedido.paciente.PacCPF}
                </td>
              </tr>
              <tr>
                <td>
                  <b>DATA NASC: </b> {pedido.paciente.PacDataNascFormatada}
                </td>
                <td>
                  <b>IDADE: </b> {pedido.paciente.PacIdade}
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <b>Data emissão: </b> {pedido.dataEmissao}
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <p>
            Solicito o encaminhamento do paciente {pedido.paciente.PacNome} para
            a especialidade {pedido.PedEspecialidade}
          </p>
          {pedido.PedIndicacao && (
            <>
              <br />
              <h5>MOTIVO: </h5> <p>{pedido.PedIndicacao}</p>
            </>
          )}

          {pedido.PedCid && (
            <>
              <h5>CID: </h5>
              <p>{pedido.PedCid}</p>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PedidoEncaminhamentoNaoAgendado;
